<template>
  <div class="form-bg">
    <div class="form-card">
      <h2 class="form-title">Invite Client</h2>
      <p class="form-subtitle">Send an invite to a new client</p>
      <v-form ref="form" v-model="valid" lazy-validation>
        <v-row class="form-row" dense>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="client.name"
              label="Name"
              variant="solo"
              rounded
              flat="true"
              bg-color="#f5f5f5"
              dense
              class="form-input"
              :rules="[v => !!v || 'Name is required']"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="client.email"
              label="Email"
              variant="solo"
              rounded
              flat="true"
              bg-color="#f5f5f5"
              dense
              class="form-input"
              :rules="[v => !!v || 'Email is required', v => /.+@.+\..+/.test(v) || 'Must be a valid email']"
            />
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              v-model="client.phone"
              label="Cellphone"
              variant="solo"
              rounded
              flat="true"
              bg-color="#f5f5f5"
              dense
              class="form-input"
              :rules="phoneValidationRules"
              @input="formatPhoneInput"
              placeholder="074 551 1155"
            />
          </v-col>
          <v-col cols="12">
            <!-- Notification Dialog -->
            <NotificationDialog
              v-model:visible="showNotification"
              :type="notificationType"
              :title="notificationTitle"
              :message="notificationMessage"
              @close="handleNotificationClose"
            />
          </v-col>
          <v-col cols="12">
            <v-btn :disabled="!valid || loading" class="form-btn" large rounded block color="#002060" @click="sendInvite">
              Send Invite
            </v-btn>
          </v-col>
        </v-row>
      </v-form>
    </div>
  </div>
</template>

<script>
import { db } from '../firebaseConfig';
import { collection, addDoc } from 'firebase/firestore';
import { ActivityLogger } from '../utils/activityLogger';
import { mapGetters } from 'vuex';
import NotificationDialog from '../components/NotificationDialog.vue';
import { formatPhoneInput, getPhoneValidationRules } from '../utils/phoneFormatter';

export default {
  components: {
    NotificationDialog
  },
  computed: {
    ...mapGetters(['user']),
    phoneValidationRules() {
      return getPhoneValidationRules();
    }
  },
  data() {
    return {
      client: {
        name: '',
        email: '',
        phone: ''
      },
      valid: false,
      loading: false,
      showNotification: false,
      notificationType: 'success',
      notificationTitle: '',
      notificationMessage: '',
    };
  },
  methods: {
    formatPhoneInput() {
      this.client.phone = formatPhoneInput(this.client.phone);
    },

    async sendInvite() {
      this.showNotification = false;
      this.loading = true;
      if (!this.client.name || !this.client.email || !this.client.phone) return;
      const newClient = {
        ...this.client,
        dateAdded: new Date().toLocaleDateString('en-US'),
      };
      try {
        await addDoc(collection(db, 'clients'), newClient);
        
        // Log client invitation activity
        const invitedBy = this.user?.displayName || this.user?.email?.split('@')[0] || 'Unknown';
        await ActivityLogger.memberInvited(invitedBy, this.user?.uid, `Client - ${newClient.email}`);
        
        // Show success notification
        this.notificationType = 'success';
        this.notificationTitle = 'Success!';
        this.notificationMessage = 'Client invite sent successfully!';
        this.showNotification = true;
      } catch (e) {
        // Show error notification
        this.notificationType = 'error';
        this.notificationTitle = 'Error!';
        this.notificationMessage = 'Failed to send invite. Please try again.';
        this.showNotification = true;
      } finally {
        this.loading = false;
      }
    },

    handleNotificationClose() {
      this.showNotification = false;
      if (this.notificationType === 'success') {
        // Reset form after successful invite
        this.client.name = '';
        this.client.email = '';
        this.client.phone = '';
      }
    },
  },
};
</script>

<style scoped>
.form-bg {
  min-height: 100vh;
  background: url('~@/assets/top.png') no-repeat center center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
.form-card {
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 32px rgba(0, 32, 96, 0.10);
  padding: 36px 32px 32px 32px;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.form-title {
  font-size: 1.7rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: 6px;
  color: #222;
}
.form-subtitle {
  font-size: 1.05rem;
  color: #666;
  text-align: center;
  margin-bottom: 22px;
}
.form-row {
  width: 100%;
}
.form-input {
  width: 100%;
}
.form-btn {
  background: #002060 !important;
  color: #fff !important;
  font-weight: 700;
  font-size: 1.1rem;
  margin-top: 8px;
  height: 48px;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(0, 32, 96, 0.2);
}
</style> 